// atoms.ts
import { atom } from "jotai";
import {
    IMPERIAL_COUNTRIES,
    AMPLIFY_GRAPHQL_LIMIT,
    MAX_USERS_ESTIMATE,
    CLIENT_LOCATION_ID,
    MAX_SERVICES_ESTIMATE,
    MB_SERVICE_CLASSIFICATION_SIMPLE,
    MB_SERVICE_CLASSIFICATION_GROUP
} from "../utils/Constants";
import { map, filter, maxBy, find, keyBy } from "lodash";
import {
    isOpenMapAnalysis,
    isWhoIsAvailableAllowed
} from "../modules/FeatureGatingModule";
import { bookingDetailsAtom } from "./booking";
import { getCurrencySymbolFromParam } from "../utils";

export const MIN_SEARCH_TERM_LENGTH = 3;
export const DEFAULT_MAX_TRAVEL_TIME_MIN = 30;

export const mbxUserAtom = atom({});

export const isMbxUserActive = atom((get) => {
    const mbxUser = get(mbxUserAtom);
    return (
        (mbxUser?.active && mbxUser?.company?.active) ||
        mbxUser?.role === "MARKETBOX_ADMIN"
    );
});

export const displaySubscriptionBannerAtom = atom(false);
export const mainNavBarHeightAtom = atom("72px");

export const companyAtom = atom((get) => get(mbxUserAtom)?.company);
export const companyProviderRestrictionConfigAtom = atom((get) => {
    const company = get(companyAtom);
    const providerRestrictionConfig = JSON.parse(
        company?.ProviderRestrictionConfig
    );
    return {
        canProviderSeeClientName:
            providerRestrictionConfig?.canProviderSeeClientName ?? true,
        canProviderSeeClientEmail:
            providerRestrictionConfig?.canProviderSeeClientEmail ?? false,
        canProviderSeeClientPhone:
            providerRestrictionConfig?.canProviderSeeClientPhone ?? true,
        canProviderSetMaxTravelTime:
            providerRestrictionConfig?.canProviderSetMaxTravelTime ?? true
    };
});
export const companyCollectsPaymentAtom = atom((get) => {
    const company = get(companyAtom);

    //company has payment collection disabled
    if (!company?.collectpayment) {
        return false;
    }

    const hasStripeConnect = Boolean(
        company?.stripeConnectEnabled && company?.stripeAccount
    );

    const hasDirectStripe = Boolean(company?.publishableStripeKey);

    return hasStripeConnect || hasDirectStripe;
});
export const suggestionsConfigAtom = atom((get) => {
    try {
        return JSON.parse(get(companyAtom)?.SuggestionConfig);
    } catch (e) {
        return {};
    }
});
export const suggestionsEnabledAtom = atom(
    (get) => get(suggestionsConfigAtom)?.isEnabled
);
export const companyMaxTravelTimeAtom = atom(
    (get) =>
        get(suggestionsConfigAtom)?.maxTravelTimeMinutes ??
        DEFAULT_MAX_TRAVEL_TIME_MIN
);

export const updateCompanyMaxTravelTimeAtom = atom(
    null,
    (get, set, newMaxTravelTimeMinutes) => {
        const currentUser = get(mbxUserAtom);

        if (currentUser && currentUser.company) {
            const currentSuggestionsConfig = JSON.parse(
                currentUser.company.SuggestionConfig
            );

            const updatedSuggestionsConfig = {
                ...currentSuggestionsConfig,
                maxTravelTimeMinutes: newMaxTravelTimeMinutes
            };

            set(mbxUserAtom, {
                ...currentUser,
                company: {
                    ...currentUser.company,
                    SuggestionConfig: JSON.stringify(updatedSuggestionsConfig)
                }
            });
        }
    }
);

export const isMbxUserAdmin = atom((get) => {
    const mbxUser = get(mbxUserAtom);
    return (
        mbxUser?.role === "MARKETBOX_ADMIN" ||
        mbxUser?.role === "COMPANY_ADMIN_PROVIDER"
    );
});
export const isMbxUserMbxAdmin = atom((get) => {
    const mbxUser = get(mbxUserAtom);
    return mbxUser?.role === "MARKETBOX_ADMIN";
});
export const isMbxUserCompanyAdminAtom = atom((get) => {
    const mbxUser = get(mbxUserAtom);
    return mbxUser?.role === "COMPANY_ADMIN_PROVIDER";
});
export const isMbxUserManager = atom((get) => {
    const mbxUser = get(mbxUserAtom);
    return mbxUser?.role === "MANAGER_PROVIDER";
});
export const isMbxUserVerifiedStaff = atom((get) => {
    const mbxUser = get(mbxUserAtom);
    return (
        mbxUser?.role === "MARKETBOX_ADMIN" ||
        mbxUser?.role === "COMPANY_ADMIN_PROVIDER" ||
        mbxUser?.role === "MANAGER_PROVIDER"
    );
});
export const isMbxUserProvider = atom((get) => {
    const mbxUser = get(mbxUserAtom);
    return mbxUser?.role === "PROVIDER";
});
export const isMbxUserClient = atom((get) => {
    const mbxUser = get(mbxUserAtom);
    return mbxUser?.role === "CLIENT";
});
export const isMbxFreeTrialUserAtom = atom((get) => {
    const mbxUser = get(mbxUserAtom);
    return mbxUser?.company?.subscriptionLevel === "FREE_TRIAL_BASIC";
});
export const mbxUserRoleFriendlyAtom = atom((get) => {
    const mbxUser = get(mbxUserAtom);

    const roleMap = {
        MARKETBOX_ADMIN: "MB Admin",
        COMPANY_ADMIN_PROVIDER: "Admin",
        MANAGER_PROVIDER: "Manager",
        PROVIDER: "Provider",
        CLIENT: "Client"
    };

    return roleMap[mbxUser.role] || "";
});
export const isImperialCountryAtom = atom((get) => {
    const mbxUser = get(mbxUserAtom);
    return IMPERIAL_COUNTRIES.includes(mbxUser?.company?.country) ?? false;
});
export const dashboardInfoAtom = atom({});
export const companyIdAtom = atom((get) => {
    const {
        company: { id }
    } = get(mbxUserAtom);
    return id;
});
export const companyCurrencyAtom = atom((get) => {
    const {
        company: { currency }
    } = get(mbxUserAtom);
    return currency;
});
export const companyCurrencySymbolAtom = atom((get) => {
    const {
        company: { currency }
    } = get(mbxUserAtom);
    return getCurrencySymbolFromParam(currency);
});
export const companyMasterTz = atom();

/**
 * data used for Integrations page
 */
export const integrationRefDataAtom = atom({});
// atoms.ts
export const providerPermalinkURL = atom((get) => {
    const mbxUser = get(mbxUserAtom);
    return `https://${mbxUser.company.subdomain}/providers`;
});
export const allUsersAtom = atom([]);

export const getUserByIdAtom = atom((get) => {
    const getUserById = (searchId) => {
        const allUsers = get(allUsersAtom);
        return allUsers.filter(({ id }) => searchId === id);
    };
    return getUserById;
});

export const allProvidersAtom = atom((get) =>
    map(filter(get(allUsersAtom), ["role", "PROVIDER"]), "provider")
);

export const searchUsersAtom = atom((get) => {
    const searchUsers = (searchTerm) => {
        const allUsers = get(allUsersAtom);
        return searchTerm && searchTerm.length > MIN_SEARCH_TERM_LENGTH
            ? allUsers.filter(
                  ({ lastname = "", firstname = "", email = "" }) => {
                      const lowerCaseSearchTerm = searchTerm.toLowerCase();
                      const searchTerms = lowerCaseSearchTerm.split(" ");

                      return searchTerms.every(
                          (term) =>
                              firstname.toLowerCase().includes(term) ||
                              lastname.toLowerCase().includes(term) ||
                              email.toLowerCase().includes(term)
                      );
                  }
              )
            : [];
    };
    return searchUsers;
});

export const getProviderByProviderIdAtom = atom((get) => {
    const getUserById = (searchId) => {
        const allUsers = get(allUsersAtom);
        const {
            provider,
            id: userId,
            ...rest
        } = allUsers.find((user) => searchId === user.provider?.id);
        return { ...provider, userId, ...rest };
    };
    return getUserById;
});
export const getUserByProviderIdAtom = atom((get) => {
    const getUserById = (providerId) => {
        const allUsers = get(allUsersAtom);
        return allUsers.find((user) => providerId === user.provider?.id);
    };
    return getUserById;
});
export const allSchedulesAtom = atom([]);
export const allServicesAtom = atom([]);
export const allCategoriesAtom = atom([]);
export const searchServicesAtom = atom((get) => {
    const searchServices = (searchTerm) => {
        const allServices = get(allServicesAtom);
        return searchTerm && searchTerm.length > MIN_SEARCH_TERM_LENGTH
            ? allServices.filter(
                  ({
                      serviceName = "",
                      categoryName = "",
                      Classification = "",
                      CourseSettings = {},
                      locations = [],
                      bookingBehaviour = "",
                      duration = "",
                      taxexempt = "",
                      price = ""
                  }) => {
                      const lowerCaseSearchTerm = searchTerm.toLowerCase();
                      const searchTerms = lowerCaseSearchTerm.split(" ");

                      return searchTerms.every(
                          (term) =>
                              serviceName.toLowerCase().includes(term) ||
                              categoryName.toLowerCase().includes(term) ||
                              Classification.toLowerCase().includes(term) ||
                              CourseSettings.capacity === term ||
                              locations.length === term ||
                              bookingBehaviour.toLowerCase().includes(term) ||
                              duration.length === term ||
                              taxexempt.toLowerCase().includes(term) ||
                              price.toLowerCase().includes(term)
                      );
                  }
              )
            : [];
    };
    return searchServices;
});
export const updateServiceInAllServicesAtom = atom(
    null,
    (get, set, updatedServiceData) => {
        const allServices = get(allServicesAtom);
        const updatedServices = allServices.map((service) =>
            service.id === updatedServiceData.id
                ? { ...service, ...updatedServiceData }
                : service
        );
        set(allServicesAtom, updatedServices);
    }
);
export const deleteServiceInAllServicesAtom = atom(
    null,
    (get, set, serviceId) => {
        const allServices = get(allServicesAtom);
        const updatedServices = allServices.filter(
            (service) => service.id !== serviceId
        );
        set(allServicesAtom, updatedServices);
    }
);
export const maxServiceDurationAtom = atom((get) =>
    maxBy(get(allActiveServicesAtom), "duration")
);
export const getProviderSchedulesByProviderIdAtom = atom((get) => {
    const getUserById = (searchId) => {
        const allProviderScedules = get(allSchedulesAtom);
        return allProviderScedules.filter(
            ({ providerId }) => searchId === providerId
        )?.provider;
    };
    return getUserById;
});

export const getProviderServicesByProviderIdAtom = atom((get) => {
    const activeServices = get(allActiveServicesAtom);
    const getUserServicesById = (searchId) => {
        const allUsers = get(allUsersAtom);
        const providerServiceIds = map(
            allUsers.find((user) => searchId === user.provider?.id)?.provider
                ?.servicetypes?.items,
            "servicetype.id"
        );
        return activeServices.filter(({ id }) =>
            providerServiceIds.includes(id)
        );
    };
    return getUserServicesById;
});
export const allLocationsAtom = atom([]);
export const allBookingsAtom = atom([]);
export const storedFutureBookingsAtom = atom([]);
export const storedPastBookingsAtom = atom([]);
export const visibleBookingsAtom = atom([]);
export const allPendingBookingsAtom = atom([]);
export const isLoadingBookingsAtom = atom(false);
export const isLoadingPendingBookingsAtom = atom(false);

export const updateAllServicesAtom = atom(null, (get, set, newService) => {
    if (!newService) return; //if null or undefined newService

    const currentServices = get(allServicesAtom);
    const serviceIndex = currentServices.findIndex(
        (service) => service.id === newService.id
    );

    //service is found in the allServicesAtom
    if (serviceIndex !== -1) {
        if (newService.deleted) {
            //remove the service if its deleted
            const updatedServices = [
                ...currentServices.slice(0, serviceIndex),
                ...currentServices.slice(serviceIndex + 1)
            ];
            set(allServicesAtom, updatedServices);
        } else {
            //update existing service
            const updatedServices = [...currentServices];
            updatedServices[serviceIndex] = newService;
            set(allServicesAtom, updatedServices);
        }
    }
    //service is not found in the allServicesAtom
    else {
        if (!newService.deleted) {
            set(allServicesAtom, [...currentServices, newService]);
        }
    }
});
export const allServicesMapAtom = atom((get) => {
    const services = get(allServicesAtom);
    return keyBy(services, "id");
});

export const allActiveServicesAtom = atom((get) => {
    const services = get(allServicesAtom);
    return services
        .filter(
            (service) =>
                service.active &&
                service.Classification !== MB_SERVICE_CLASSIFICATION_GROUP
        )
        .sort((a, b) => a.name.localeCompare(b.name));
});

export const allActiveSimpleServicesAtom = atom((get) => {
    const services = get(allServicesAtom);
    return services.filter(
        (service) =>
            service.active &&
            service.Classification === MB_SERVICE_CLASSIFICATION_SIMPLE
    );
});

export const allGroupServicesAtom = atom((get) => {
    const services = get(allServicesAtom);
    return services.filter(
        (service) => service.Classification === MB_SERVICE_CLASSIFICATION_GROUP
    );
});

export const allSkillsAtom = atom([]);
export const updateAllSkillsAtom = atom(null, (get, set, newSkill) => {
    if (!newSkill) return; //if null or undefined, do nothing

    const currentSkills = get(allSkillsAtom);
    const skillIndex = currentSkills.findIndex(
        (skill) => skill.id === newSkill.id
    );

    //skill is found in the allSkillsAtom
    if (skillIndex !== -1) {
        if (newSkill.deleted) {
            //remove the skill if its deleted
            const updatedSkills = [
                ...currentSkills.slice(0, skillIndex),
                ...currentSkills.slice(skillIndex + 1)
            ];
            set(allSkillsAtom, updatedSkills);
        } else {
            //update existing skill
            const updatedSkills = [...currentSkills];
            updatedSkills[skillIndex] = newSkill;
            set(allSkillsAtom, updatedSkills);
        }
    }
    //skill is not found in the allSkillsAtom
    else {
        if (!newSkill.deleted) {
            set(allSkillsAtom, [...currentSkills, newSkill]);
        }
    }
});
export const allActiveSkillsAtom = atom((get) => {
    const skill = get(allSkillsAtom);
    return skill.filter((skill) => skill.active);
});

export const allActiveLocationsAtom = atom((get) => {
    const locations = get(allLocationsAtom);
    return locations.filter(
        (location) => !(location.deleted === true || location.active === false)
    );
});

export const updateAllActiveLocationsAtom = atom(
    null,
    (get, set, newLocation) => {
        const currentLocations = get(allActiveLocationsAtom);
        const locationIndex = currentLocations.findIndex(
            (location) => location.id === newLocation.id
        );
        if (locationIndex !== -1) {
            const updatedLocations = [...currentLocations];
            updatedLocations[locationIndex] = newLocation;
            set(allLocationsAtom, updatedLocations);
        } else {
            set(allLocationsAtom, [...currentLocations, newLocation]);
        }
    }
);

export const updateUserInAllUsersAtom = atom(
    null,
    (get, set, updatedUserData) => {
        const allUsers = get(allUsersAtom);
        const updatedUsers = allUsers.map((user) =>
            user.id === updatedUserData.id
                ? { ...user, ...updatedUserData }
                : user
        );
        set(allUsersAtom, updatedUsers);
    }
);

//Write atom that updates allSchedulesAtom by removing oldRecords and storing newRecords
export const updateAllSchedulesAtom = atom(
    null,
    (get, set, oldRecords, newRecords) => {
        const currentSchedules = get(allSchedulesAtom);
        const updatedSchedules = currentSchedules.filter(
            (record) =>
                !oldRecords.some(
                    (oldRecord) =>
                        record.id === oldRecord.id &&
                        record.scheduleinfo.includes(oldRecord.scheduleinfo)
                )
        );
        const finalSchedules = [...updatedSchedules, ...newRecords];
        set(allSchedulesAtom, finalSchedules);
    }
);

export const deleteUserInAllUsersAtom = atom(null, (get, set, userId) => {
    const allUsers = get(allUsersAtom);
    const updatedUsers = allUsers.filter((user) => user.id !== userId);
    set(allUsersAtom, updatedUsers);
});

export const updateBookingInVisibleBookingsAtom = atom(
    null,
    (get, set, updatedBookingData) => {
        const visibleBookings = get(visibleBookingsAtom);
        const updatedBookings = visibleBookings.map((booking) => {
            return booking.id === updatedBookingData.id
                ? { ...booking, ...updatedBookingData }
                : booking;
        });
        set(visibleBookingsAtom, updatedBookings);
    }
);

export const updateOrderNotesInVisibleBookingsAtom = atom(
    null,
    (get, set, { orderId, updatedOrderData }) => {
        const visibleBookings = get(visibleBookingsAtom);

        const updatedBookings = visibleBookings.map((booking) =>
            booking.order?.id === orderId
                ? { ...booking, ...updatedOrderData }
                : booking
        );

        set(visibleBookingsAtom, updatedBookings);
    }
);

export const updateProviderInVisibleBookingsAtom = atom(
    null,
    (get, set, updatedBookingData) => {
        const visibleBookings = get(visibleBookingsAtom);
        const updatedBookings = visibleBookings.map((booking) =>
            booking.providerId === updatedBookingData.providerId
                ? { ...booking, ...updatedBookingData }
                : booking
        );
        set(visibleBookingsAtom, updatedBookings);
    }
);

export const updateClientInVisibleBookingsAtom = atom(
    null,
    (get, set, updatedBookingData) => {
        const visibleBookings = get(visibleBookingsAtom);
        const updatedBookings = visibleBookings.map((booking) =>
            booking.client.userId === updatedBookingData.clientId
                ? { ...booking, ...updatedBookingData }
                : booking
        );
        set(visibleBookingsAtom, updatedBookings);
    }
);

export const cancelBookingInVisibleBookingsAtom = atom(
    null,
    (get, set, bookingId) => {
        const visibleBookings = get(visibleBookingsAtom);
        const updatedBookings = visibleBookings.filter(
            (booking) => booking.id !== bookingId
        );
        set(visibleBookingsAtom, updatedBookings);
    }
);

export const conformationDialogOpenAtom = atom(false);
export const conformationDialogContentAtom = atom("");
export const conformationDialogTitleAtom = atom("");
export const conformationDialogSubmitButtonAtom = atom("Confirm");
export const hideCancelButtonAtom = atom(false);
export const conformationDialogIsSubmittingAtom = atom(false);
export const warningIconInHeadingAtom = atom(false);
const fnAtom = atom({ fn: () => {} });
export const confirmationFunctionAtom = atom(
    (get) => get(fnAtom),
    (get, set, newFn) => {
        set(fnAtom, { fn: newFn });
    }
);
export const onSaveFunctionAtom = atom(
    (get) => get(fnAtom),
    (get, set, newFn) => {
        set(fnAtom, { fn: newFn });
    }
);

export const onCreateBookingFunctionAtom = atom(
    (get) => get(fnAtom),
    (get, set, newFn) => {
        set(fnAtom, { fn: newFn });
    }
);

export const showCompanySettingsModalAtom = atom(false);
export const isLoadingUsersAtom = atom(false);
export const loadingUsersProgressAtom = atom(5); // so we some progress onload
export const incrementLoadingUsersProgressAtom = atom(null, (get, set) => {
    set(
        loadingUsersProgressAtom,
        get(loadingUsersProgressAtom) +
            (AMPLIFY_GRAPHQL_LIMIT / MAX_USERS_ESTIMATE) * 100
    );
});
export const isLoadingServicesAtom = atom(false);
export const loadingServicesProgressAtom = atom(5);
export const incrementLoadingServicesProgressAtom = atom(null, (get, set) => {
    set(
        loadingUsersProgressAtom,
        get(loadingUsersProgressAtom) +
            (AMPLIFY_GRAPHQL_LIMIT / MAX_SERVICES_ESTIMATE) * 100
    );
});
export const clientPackagesAtom = atom([]);
export const snackBarAtom = atom({ open: false, message: "", duration: 2000 });

export const isMapAnalysisAllowedAtom = atom((get) => {
    const mbxUser = get(mbxUserAtom);
    const isVerified = get(isMbxUserVerifiedStaff);
    return isVerified && isOpenMapAnalysis(mbxUser?.company);
});
export const openFeatureGateDialogAtom = atom(false);
export const openAddClientModalAtom = atom(false);
export const openImportClientsModalAtom = atom(false);

/* export const getLocationById = (
    allActiveLocations,
    locationId,
    clientAddress
) => {
    if (locationId === CLIENT_LOCATION_ID) {
        return clientAddress;
    }
    return find(allActiveLocations, ["id", locationId]);
}; */

//getLocationByIdAtom
export const getLocationByIdAtom = atom((get) => {
    const allActiveLocations = get(allActiveLocationsAtom);
    const { clientAddress, provider } = get(bookingDetailsAtom);
    const getLocationById = (locationId) => {
        if (locationId === CLIENT_LOCATION_ID) {
            return {
                ...clientAddress,
                id: CLIENT_LOCATION_ID,
                timezone: provider?.timezone
            };
        }
        return find(allActiveLocations, ["id", locationId]);
    };
    return getLocationById;
});
